import { VideoWithEffects } from "./VideoWithEffects";
import { Layer } from "../types/Editor";
import { ComponentType, useCallback, useRef, useState, useEffect } from "react";

export interface VideoWithEffectsProps {
  videoSrc: string;
  layers: Layer[];
  isPaused: boolean;
  onLayerUpdate: (layer: Layer) => void;
  activeLayerId?: string;
  isDrawing: boolean;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  activeLayer?: Layer | null;
  cursorPosition?: { x: number; y: number } | null;
  currentTool?: "highlight" | "zoom" | "blur" | null;
}

const VideoWithEffectsWrapper: ComponentType<VideoWithEffectsProps> = (
  props
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startDimsRef = useRef({ width: 0, height: 0 });
  const startLayerPosRef = useRef({ x: 0, y: 0 }); // Add this to store initial layer position
  const activeLayerRef = useRef<Layer | null>(null);

  const {onMouseDown, onMouseUp, onMouseMove, onMouseLeave} = props;

  useEffect(() => {
    if (props.activeLayerId) {
      const activeLayer = props.layers.find(
        (layer) => layer.id === props.activeLayerId
      );
      if (activeLayer) {
        activeLayerRef.current = activeLayer;
      }
    } else {
      activeLayerRef.current = null;
    }
  }, [props.activeLayerId, props.layers]);

  const getRelativeCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current) return { x: 0, y: 0 };
      const rect = containerRef.current.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / rect.width,
        y: (clientY - rect.top) / rect.height,
      };
    },
    []
  );

  const handleMouseDown = useCallback(
    (
      e: React.MouseEvent,
      layer: Layer,
      action: "drag" | "resize",
      handle?: string
    ) => {
      e.stopPropagation();
      e.preventDefault();

      const { x, y } = getRelativeCoordinates(e.clientX, e.clientY);
      startPosRef.current = { x, y };
      startDimsRef.current = {
        width: layer.data.width,
        height: layer.data.height,
      };
      startLayerPosRef.current = { x: layer.data.x, y: layer.data.y }; // Store initial layer position
      activeLayerRef.current = layer;

      if (action === "drag") {
        setIsDragging(true);
      } else {
        setIsResizing(handle || null);
      }
    },
    [getRelativeCoordinates]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if ((!isDragging && !isResizing) || !activeLayerRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const { x: currentX, y: currentY } = getRelativeCoordinates(
        e.clientX,
        e.clientY
      );
      const deltaX = currentX - startPosRef.current.x;
      const deltaY = currentY - startPosRef.current.y;

      const layer = activeLayerRef.current;
      const newData = { ...layer.data };
      const minSize = 0.05;
      const { width: startWidth, height: startHeight } = startDimsRef.current;
      const { x: startX, y: startY } = startLayerPosRef.current; // Use stored initial position

      if (isDragging) {
        newData.x = Math.max(
          0,
          Math.min(1 - layer.data.width, layer.data.x + deltaX)
        );
        newData.y = Math.max(
          0,
          Math.min(1 - layer.data.height, layer.data.y + deltaY)
        );
        startPosRef.current = { x: currentX, y: currentY };
      } else if (isResizing) {
        switch (isResizing) {
          case "top-left":
            newData.width = Math.max(minSize, startWidth - deltaX);
            newData.height = Math.max(minSize, startHeight - deltaY);
            newData.x = Math.max(
              0,
              Math.min(startX + deltaX, startX + startWidth - minSize)
            );
            newData.y = Math.max(
              0,
              Math.min(startY + deltaY, startY + startHeight - minSize)
            );
            break;

          case "top":
            newData.height = Math.max(minSize, startHeight - deltaY);
            newData.y = Math.max(
              0,
              Math.min(startY + deltaY, startY + startHeight - minSize)
            );
            break;

          case "top-right":
            newData.width = Math.max(minSize, startWidth + deltaX);
            newData.height = Math.max(minSize, startHeight - deltaY);
            newData.y = Math.max(
              0,
              Math.min(startY + deltaY, startY + startHeight - minSize)
            );
            break;

          case "right":
            newData.width = Math.max(minSize, startWidth + deltaX);
            break;

          case "bottom-right":
            newData.width = Math.max(minSize, startWidth + deltaX);
            newData.height = Math.max(minSize, startHeight + deltaY);
            break;

          case "bottom":
            newData.height = Math.max(minSize, startHeight + deltaY);
            break;

          case "bottom-left":
            newData.width = Math.max(minSize, startWidth - deltaX);
            newData.height = Math.max(minSize, startHeight + deltaY);
            newData.x = Math.max(
              0,
              Math.min(startX + deltaX, startX + startWidth - minSize)
            );
            break;

          case "left":
            newData.width = Math.max(minSize, startWidth - deltaX);
            newData.x = Math.max(
              0,
              Math.min(startX + deltaX, startX + startWidth - minSize)
            );
            break;
        }
      }

      // Ensure the effect stays within bounds
      newData.x = Math.max(0, Math.min(1 - newData.width, newData.x));
      newData.y = Math.max(0, Math.min(1 - newData.height, newData.y));
      newData.width = Math.min(newData.width, 1 - newData.x);
      newData.height = Math.min(newData.height, 1 - newData.y);

      props.onLayerUpdate({
        ...layer,
        data: newData,
      });
    },
    [isDragging, isResizing, getRelativeCoordinates, props]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
    activeLayerRef.current = null;
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div ref={containerRef} className="bleku relative w-full h-full"
    onMouseDown={onMouseDown}
    onMouseMove={onMouseMove}
    onMouseUp={onMouseUp}
    onMouseLeave={onMouseLeave}
    >
      <VideoWithEffects
        videoSrc={props.videoSrc}
        layers={props.layers}
        isPaused={props.isPaused}
        onLayerUpdate={props.onLayerUpdate}
        activeLayerId={props.activeLayerId}
        isDrawing={props.isDrawing}
        onMouseDown={handleMouseDown}
        isDragging={isDragging}
        isResizing={isResizing}
      />
      
      {/* Drawing Box */}
      {props.isDrawing && props.activeLayer && (
        <div
          className="absolute pointer-events-none z-30"
          style={{
            left: `${props.activeLayer.data.x * 100}%`,
            top: `${props.activeLayer.data.y * 100}%`,
            width: `${props.activeLayer.data.width * 100}%`,
            height: `${props.activeLayer.data.height * 100}%`,
            border: "2px solid white",
            borderRadius: props.activeLayer.type === "highlight" ? "8px" : props.activeLayer.type === "blur" ? "12px" : "4px",
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.4)",
            backgroundColor:
              props.activeLayer.type === "highlight"
                ? "rgba(251, 191, 36, 0.2)"
                : props.activeLayer.type === "blur"
                  ? "rgba(192, 132, 252, 0.2)"
                  : "transparent",
          }}
        >
          <div className="absolute -top-8 left-0 bg-white px-2 py-0.5 rounded-lg text-[10px] font-bold text-slate-900 shadow-lg border border-slate-100 uppercase tracking-wider">
            New {props.activeLayer.type}
          </div>
        </div>
      )}

      {/* Cursor overlay */}
      {!props.isDrawing && props.cursorPosition && props.currentTool && (
        <div
          style={{
            position: "absolute",
            left: `${props.cursorPosition.x * 100}%`,
            top: `${props.cursorPosition.y * 100}%`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
          className={`w-10 h-10 ${
            props.currentTool === "highlight"
              ? "border-2 border-amber-400 bg-amber-400/10 shadow-[0_0_15px_rgba(251,191,36,0.3)] rounded-full flex items-center justify-center"
              : props.currentTool === "blur"
              ? "border-2 border-purple-400 bg-purple-400/10 shadow-[0_0_15px_rgba(192,132,252,0.3)] rounded-full flex items-center justify-center"
              : "flex items-center justify-center"
          } transition-all z-40`}
        >
          {props.currentTool === "zoom" ? (
            <>
              <div className="absolute w-full h-0.5 bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.8)] rounded-full" />
              <div className="absolute w-0.5 h-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.8)] rounded-full" />
              <div className="absolute w-1.5 h-1.5 bg-white rounded-full shadow-[0_0_5px_rgba(255,255,255,1)]" />
            </>
          ) : (
            <span className="text-white font-bold drop-shadow-sm">+</span>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoWithEffectsWrapper;
